/**
 * @file Vue mixin for methods related to styling surface using settings page (with preview data and real data)
 */
/* eslint-disable class-methods-use-this */
import { processedUrl } from '@padlet/vivaldi-client'
import { getDisplayAttributes } from 'bits/beethoven'
import { $ } from 'bits/jquery'
import { Component, Vue, Watch } from 'vue-property-decorator'
import { Getter, namespace } from 'vuex-class'

enum Fill {
  cover = 'cover',
  tile = 'auto',
  auto = 'auto',
}

const settings = namespace('settings')

@Component
class PageStylingMixin extends Vue {
  latestBackgroudUpdateTime = new Date()

  @Getter title
  @Getter portrait
  @Getter fontId
  @Getter originalBackground
  @Getter background
  @Getter backgroundColor
  @Getter backgroundLuminance
  @Getter colorScheme
  @Getter useArvo
  @settings.Getter isPreviewing
  @settings.Action updateBackgroundMetadata

  @Watch('title')
  setTitle(newValue): void {
    document.title = newValue
  }

  @Watch('portrait')
  setIcon(newValue): void {
    const faviconElement = document.querySelector("link[rel='icon']") as HTMLLinkElement
    if (faviconElement) {
      faviconElement.href = newValue || '/favicon.ico'
    }

    const appleTouchIconElement = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement
    if (appleTouchIconElement) {
      appleTouchIconElement.href = newValue || '/apple-touch-icon.png'
    }
  }

  @Watch('fontId')
  setFont(newFontId): void {
    $('[data-font]').attr('data-font', newFontId)
  }

  @Watch('backgroundLuminance')
  setBackgroundLuminance(luminanceName): void {
    $('[data-behavior~="background-holder"]').attr('data-background-luminance', luminanceName)
  }

  @Watch('backgroundColor')
  setHeaderColor(colorAsRgb): void {
    $('meta[name=theme-color]').attr('content', colorAsRgb)
    document.documentElement.style.backgroundColor = colorAsRgb
  }

  @Watch('colorScheme')
  setColorScheme(newColorScheme): void {
    $('[data-color-scheme]').attr('data-color-scheme', newColorScheme)
  }

  isUpdateStale(timestamp): boolean {
    return this.latestBackgroudUpdateTime !== timestamp
  }

  async fetchBackgroundMetadata(url, timestamp): Promise<void> {
    const data = await getDisplayAttributes(url, { dpr: 1.0, locale: 'en' })
    if (data) {
      const { image_color: dominantColourAsRgb, image_luminance: imageLuminance } = data
      if (this.isUpdateStale(timestamp)) return
      this.updateBackgroundMetadata({ dominantColourAsRgb, imageLuminance })
    }
  }

  setBackground(url, fill: string): void {
    $('[data-behavior~="background-holder"]').css({
      backgroundSize: Fill[fill],
      backgroundImage: `url(${url})`,
    })
  }

  @Watch('background')
  updateBackground(newBackground, oldBackground): void {
    if (
      oldBackground.url === newBackground.url &&
      oldBackground.fill === newBackground.fill &&
      oldBackground.effect === newBackground.effect
    )
      return

    // Show tiny version of the new background first, in case background is huge
    const obscureBackgroundUrl = processedUrl(newBackground.url, { preset: 'obscure' })
    this.setBackground(obscureBackgroundUrl, Fill.cover)

    const thisUpdateTimestamp = new Date()
    this.latestBackgroudUpdateTime = thisUpdateTimestamp

    const transforms = {
      preset: 'background',
      width: window.screen.width,
      height: window.screen.height,
      effect: newBackground.effect === 'blur' ? 'blur:10' : undefined,
    }
    const processedBackgroundUrl = processedUrl(newBackground.url, transforms)

    const image = new Image()
    image.onload = (): void => {
      if (this.isUpdateStale(thisUpdateTimestamp)) return

      let backgroundSize = Fill.auto
      if (newBackground.fill && newBackground.fill !== 'undefined') {
        backgroundSize = newBackground.fill
      } else if (image.width >= 800) {
        backgroundSize = Fill.cover
      }

      // Expect metadata to be provided when change is triggered remotely.
      // Skip metadata update if we are reverting to original background since it is already present.
      if (this.isPreviewing && this.originalBackground !== newBackground) {
        this.fetchBackgroundMetadata(newBackground.url, thisUpdateTimestamp)
      }

      this.setBackground(processedBackgroundUrl, backgroundSize)
    }
    image.src = processedBackgroundUrl
  }
}

export default PageStylingMixin
